import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { updateNavbarHeading } from "../../../../constants/layout/store/filterSlice";
import { API_URL } from "../../../../constants";
import { GetServiceCaller } from "../../../../services/serviceCaller";
import { Table, Form } from "react-bootstrap";
import Pagination from "../../../../components/Pagination";
import SearchBar from "../../../../components/SearchBar";
import ExportToExcel from "../../../../components/ExportToExcel";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaSpinner } from "react-icons/fa";
import Select from "react-select";

const LeaveSummary = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(updateNavbarHeading("Leave Summary"));
  }, [dispatch]);

  const [searchTerm, setSearchTerm] = useState("");
  const [ledger, setLedger] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [monthList, setMonthList] = useState([]);
  const [apiStatus, setApiStatus] = useState(undefined);
  const [showDropdown, setShowDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Column visibility configuration
  const [columnVisibility, setColumnVisibility] = useState({
    EmployeeName: true,
    LeaveType: true,
    LeaveAccrued: true,
    LeaveApplied: true,
    LeaveApproved: true,
    LeaveBalance: true,
  });

  const columns = [
    { id: "EmployeeName", label: "Employee", accessor: "EmployeeName" },
    { id: "LeaveType", label: "Leave Type", accessor: "LeaveType" },
    { id: "LeaveAccrued", label: "Leave Accrued", accessor: "LeaveAccrued" },
    { id: "LeaveApplied", label: "Leave Applied", accessor: "LeaveApplied" },
    { id: "LeaveApproved", label: "Leave Approved", accessor: "LeaveApproved" },
    { id: "LeaveBalance", label: "Leave Balance", accessor: "LeaveBalance" },
  ];

  const fetchReportList = async (employeeId = null, monthId = null) => {
    try {
      setIsLoading(true);
      const response = await GetServiceCaller(
        `${API_URL}leave-summary?P_EmployeeID=${employeeId || ""}&P_LeaveID=${
          monthId || ""
        }`
      );

      if (response?.data) {
        const ledger = response.data.leaveSummary || [];
        setLedger(ledger);

        // Employee list
        const employeeList = (response.data.empList || []).map((tech) => ({
          value: tech.value,
          label: tech.Name,
        }));
        setEmployeeList(employeeList);

        // Month list
        const monthlist = (response.data.leaveList || []).map((tech) => ({
          value: tech.value,
          label: tech.text,
        }));
        setMonthList(monthlist);
        setApiStatus(response.status);
      }
    } catch (error) {
      console.error("Error fetching reports", error);
      setLedger([]);
      setEmployeeList([]);
      setMonthList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReportList();
  }, []);

  // Sorting function
  const sortedData = useMemo(() => {
    let sortableData = [...ledger];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        // Handle numeric fields differently
        const numericFields = [
          "LeaveAccrued",
          "LeaveApplied",
          "LeaveApproved",
          "LeaveBalance",
        ];
        const isNumeric = numericFields.includes(sortConfig.key);

        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (isNumeric) {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else {
          aValue = (aValue || "").toString().toLowerCase();
          bValue = (bValue || "").toString().toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [ledger, sortConfig]);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return sortedData;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return sortedData.filter((item) => {
      return Object.keys(item).some((key) => {
        // Only search in string fields
        if (typeof item[key] === "string") {
          return item[key].toLowerCase().includes(lowerSearchTerm);
        }
        return false;
      });
    });
  }, [sortedData, searchTerm]);

  // Pagination calculations
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Get current items for pagination
  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage, totalItems]);

  const recordsPerPageOptions = [
    { value: 10, label: "10" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
    { value: filteredData.length, label: "All" },
  ];

  const toggleColumnVisibility = (columnId) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Sort handler
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  // Get sort icon for header
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-chevron-down"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-chevron-up"></i>
    ) : (
      <i className="bi bi-chevron-down"></i>
    );
  };

  const handleGetData = () => {
    fetchReportList(selectedEmployee?.value, selectedMonth?.value);
    setShowDropdown(false);
  };

  // Handle reset button click
  const handleReset = () => {
    setSelectedEmployee(null);
    setSelectedMonth(null);
    setSearchTerm("");
    fetchReportList();
    setShowDropdown(false);
  };

  const totals = useMemo(() => {
    const total = {
      LeaveAccrued: 0,
      LeaveApplied: 0,
      LeaveApproved: 0,
      LeaveBalance: 0,
    };

    filteredData.forEach((item) => {
      total.LeaveAccrued += parseFloat(item.LeaveAccrued || 0);
      total.LeaveApplied += parseFloat(item.LeaveApplied || 0);
      total.LeaveApproved += parseFloat(item.LeaveApproved || 0);
      total.LeaveBalance += parseFloat(item.LeaveBalance || 0);
    });

    return total;
  }, [filteredData]);

  const totalcount = filteredData.length;

  return (
    <div className="container-fluid" id="skill_section">
      <div className="container-fluid" id="skill-table-area">
       
          <div className="d-flex justify-content-between">
            <div className="d-flex">
              <div className="me-1">
                <SearchBar
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  placeholder="Search by employee or leave type..."
                />
              </div>
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
              >
                <div className="btn-group me-2">
                  <button
                    type="button"
                    className="dropdown-toggle skill-button"
                    data-bs-toggle="dropdown"
                    onClick={() => setShowDropdown(!showDropdown)}
                    aria-expanded="false"
                    id="excel-btn"
                  >
                    <i className="bi bi-funnel-fill"></i>
                  </button>
                  <ul
                    className={`dropdown-menu p-2 project-status-filter ${
                      showDropdown ? "show" : ""
                    }`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="row">
                      <div className="col-md-4">
                        <div>
                          <label
                            htmlFor="employee-select"
                            className="form-label"
                          >
                            Employee
                          </label>
                          <Select
                            id="employee-select"
                            options={employeeList}
                            value={selectedEmployee}
                            onChange={setSelectedEmployee}
                            placeholder="Select Employee"
                            isClearable
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div>
                          <label htmlFor="month-select" className="form-label">
                            Leave
                          </label>
                          <Select
                            id="month-select"
                            options={monthList}
                            value={selectedMonth}
                            onChange={setSelectedMonth}
                            placeholder="Select Month"
                            isClearable
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="text-end">
                        <button
                          className="custom-btn mx-2"
                          onClick={handleReset}
                        >
                          Reset
                        </button>
                        <button className="custom-btn" onClick={handleGetData}>
                          Get Data
                        </button>
                      </div>
                    </div>
                  </ul>
                </div>
              </OverlayTrigger>
            </div>
            <div className="d-flex justify-content-end">
              <div className="me-1">
                <ExportToExcel
                  data={filteredData}
                  filename="Leave-Summary.csv"
                  columns={columns.filter((col) => columnVisibility[col.id])}
                />
              </div>
              <div className="d-flex justify-content-end">
                <div className="me-1">
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-top">Column Chooser</Tooltip>}
                  >
                    <div className="btn-group me-2">
                      <button
                        type="button"
                        className="dropdown-toggle skill-button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        id="excel-btn"
                      >
                        <i className="bi bi-list"></i>
                      </button>
                      <ul className="dropdown-menu p-2">
                        <div className="row">
                          <Form.Group>
                            {columns.map((column) => (
                              <Form.Check
                                key={column.id}
                                type="checkbox"
                                id={`checkbox-${column.id}`}
                                label={column.label}
                                checked={columnVisibility[column.id]}
                                onChange={() =>
                                  toggleColumnVisibility(column.id)
                                }
                              />
                            ))}
                          </Form.Group>
                        </div>
                      </ul>
                    </div>
                  </OverlayTrigger>
                </div>
              </div>
            </div>
          </div>
 <div className="table-responsive">
          <Table bordered striped hover>
            <thead id="thcolor" className="text-center">
              <tr>
                {columns.map(
                  (column) =>
                    columnVisibility[column.id] && (
                      <th
                        key={column.id}
                        onClick={() => requestSort(column.accessor)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-center">
                          {column.label}
                          {getSortIcon(column.accessor)}
                        </div>
                      </th>
                    )
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    <div className="d-flex justify-content-center">
                      <FaSpinner className="spinner" />
                    </div>
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                <>
                  {currentItems.map((item, index) => (
                    <tr key={index}>
                      {columns.map(
                        (column) =>
                          columnVisibility[column.id] && (
                            <td
                              key={`${index}-${column.id}`}
                              className={
                                [
                                  "LeaveAccrued",
                                  "LeaveApplied",
                                  "LeaveApproved",
                                  "LeaveBalance",
                                ].includes(column.id)
                                  ? "text-end"
                                  : "text-start"
                              }
                            >
                              {[
                                "LeaveAccrued",
                                "LeaveApplied",
                                "LeaveApproved",
                                "LeaveBalance",
                              ].includes(column.id)
                                ? parseFloat(
                                    item[column.accessor] || 0
                                  ).toFixed(2)
                                : item[column.accessor] || "-"}
                            </td>
                          )
                      )}
                    </tr>
                  ))}
                  {/* Summary Row */}
                  <tr className="fw-bold bg-light">
                    {columns.map((column) => {
                      if (!columnVisibility[column.id]) return null;

                      if (column.id === "EmployeeName") {
                        return (
                          <td key={column.id} className="text-start">
                            Count: {totalcount}
                          </td>
                        );
                      }

                      if (
                        [
                          "LeaveAccrued",
                          "LeaveApplied",
                          "LeaveApproved",
                          "LeaveBalance",
                        ].includes(column.id)
                      ) {
                        return (
                          <td key={column.id} className="text-end">
                            {totals[column.id].toFixed(2)}
                          </td>
                        );
                      }

                      return <td key={column.id}></td>;
                    })}
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
  </div>
          {!isLoading && (
            <div className="d-flex justify-content-between mb-3">
              <div>
                <span className="d-flex align-items-center">
                  Showing {(currentPage - 1) * itemsPerPage + 1} -{" "}
                  {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                  {totalItems}
                  <select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="mx-2 form-select form-select-sm"
                    style={{ width: "auto" }}
                  >
                    {recordsPerPageOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </span>
              </div>
              <div>
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          )}
        </div>
    
    </div>
  );
};

export default LeaveSummary;
